"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Keyboard } from "lucide-react";

interface KeyboardShortcut {
  keys: string[];
  description: string;
  category: string;
}

const shortcuts: KeyboardShortcut[] = [
  // Navigation
  {
    keys: ["Tab"],
    description: "Navigate forward through interactive elements",
    category: "Navigation",
  },
  {
    keys: ["Shift", "Tab"],
    description: "Navigate backward through interactive elements",
    category: "Navigation",
  },
  {
    keys: ["Enter"],
    description: "Activate buttons, links, and form submissions",
    category: "Navigation",
  },
  {
    keys: ["Space"],
    description: "Activate buttons and toggle checkboxes",
    category: "Navigation",
  },
  {
    keys: ["Escape"],
    description: "Close dialogs, menus, and modals",
    category: "Navigation",
  },
  {
    keys: ["Arrow Keys"],
    description: "Navigate within menus, dropdowns, and date pickers",
    category: "Navigation",
  },

  // Application Shortcuts
  {
    keys: ["Cmd/Ctrl", "K"],
    description: "Open global search",
    category: "Application",
  },
  {
    keys: ["?"],
    description: "Show keyboard shortcuts help (this dialog)",
    category: "Application",
  },

  // Forms
  {
    keys: ["Cmd/Ctrl", "Enter"],
    description: "Submit current form (when in a form field)",
    category: "Forms",
  },

  // Accessibility
  {
    keys: ["Skip Link"],
    description: "Press Tab on page load to reveal skip-to-content link",
    category: "Accessibility",
  },
];

interface KeyboardShortcutsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function KeyboardShortcutsDialog({
  isOpen,
  onClose,
}: KeyboardShortcutsDialogProps) {
  const categories = Array.from(new Set(shortcuts.map((s) => s.category)));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Keyboard className="h-5 w-5 text-primary" />
            <DialogTitle>Keyboard Shortcuts</DialogTitle>
          </div>
          <DialogDescription>
            Navigate the application efficiently using these keyboard shortcuts
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {categories.map((category) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                {category}
              </h3>
              <div className="space-y-2">
                {shortcuts
                  .filter((s) => s.category === category)
                  .map((shortcut, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between gap-4 py-2 px-3 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <span className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                        {shortcut.description}
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        {shortcut.keys.map((key, keyIndex) => (
                          <span key={keyIndex} className="flex items-center">
                            <kbd className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 text-xs font-semibold text-gray-700 bg-gray-100 border border-gray-300 rounded dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">
                              {key}
                            </kbd>
                            {keyIndex < shortcut.keys.length - 1 && (
                              <span className="mx-1 text-gray-400">+</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t pt-4 mt-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            <strong>Note:</strong> "Cmd" refers to the Command key on Mac, and "Ctrl" refers to the Control key on Windows/Linux.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Hook to enable keyboard shortcuts help dialog
 * Press "?" to open the shortcuts dialog
 */
export function useKeyboardShortcuts() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Open shortcuts dialog with "?"
      if (e.key === "?" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        // Don't trigger if user is typing in an input
        const target = e.target as HTMLElement;
        if (
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable
        ) {
          return;
        }
        e.preventDefault();
        setIsOpen(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return {
    isOpen,
    openShortcuts: () => setIsOpen(true),
    closeShortcuts: () => setIsOpen(false),
  };
}
